import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Group,
  LoadingOverlay,
  ScrollArea,
  Table,
  Text,
  Title,
  Badge,
  TextInput,
  PasswordInput,
} from "@mantine/core";
import {
  getAssignLicenseRequests,
  approveAssignLicenseRequest,
} from "@utils/api-communication/RestApiCli";
import { AssignLicenseRequest } from "../../../types/licenseAssignmentTypes";
import { modals } from "@mantine/modals";

export default function LicenseAssignmentRequests() {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<AssignLicenseRequest[]>([]);

  const loadRequests = () => {
    setLoading(true);
    setError(null);
    getAssignLicenseRequests(
      (resp) => {
        const data = resp?.data ?? [];
        setRequests(data as AssignLicenseRequest[]);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load requests");
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const onApprove = (req: AssignLicenseRequest) => {
    let orgId = req.org?.orgId || "";
    let password = "";

    const handleSubmit = () => {
      if (!orgId || !password) {
        setError("Please enter both orgId and password");
        return;
      }
      setLoading(true);
      approveAssignLicenseRequest(
        { requestId: req._id, orgId, password },
        () => {
          modals.closeAll();
          loadRequests();
        },
        (err) => {
          setLoading(false);
          setError(err?.response?.data?.message || err?.message || "Failed to approve request");
        },
      );
    };

    modals.open({
      title: "Approve Assign License Request",
      children: (
        <>
          <Text size="sm" mb="xs">
            Enter the organization ID (username) and your password to approve this request.
          </Text>
          <TextInput
            label="Org ID (username)"
            placeholder="org-xxxx"
            defaultValue={orgId}
            onChange={(e) => (orgId = e.currentTarget.value)}
            mb="sm"
          />
          <PasswordInput
            label="Password"
            placeholder="Your password"
            onChange={(e) => (password = e.currentTarget.value)}
            mb="md"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => modals.closeAll()}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>Submit</Button>
          </Group>
        </>
      ),
    });
  };

  const rows = useMemo(
    () =>
      (requests || []).map((r) => {
        const created = r?.createdAt ? new Date(r.createdAt as any) : null;
        return (
          <Table.Tr key={r._id}>
            <Table.Td>
              <Text size="sm" fw={500}>
                {r._id}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{r.distId}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" fw={500}>
                {r.org?.name || "-"}
              </Text>
              <Text size="xs" c="dimmed">
                {r.org?.orgId || ""}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{r.licenseId}</Text>
            </Table.Td>
            <Table.Td>
              {r.emailsToInform && r.emailsToInform.length > 0 ? (
                <Text size="sm">{r.emailsToInform.join(", ")}</Text>
              ) : (
                <Text size="sm" c="dimmed">
                  -
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              <Group gap="xs">
                {r.silent ? <Badge color="gray">silent</Badge> : <Badge color="blue">notify</Badge>}
                {r.isApproved ? (
                  <Badge color="green">approved</Badge>
                ) : (
                  <Badge color="yellow">pending</Badge>
                )}
              </Group>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{created ? created.toLocaleString() : "-"}</Text>
            </Table.Td>
            <Table.Td>
              <Group gap="xs" justify="right">
                <Button
                  size="xs"
                  variant="light"
                  disabled={!!r.isApproved}
                  onClick={() => onApprove(r)}
                >
                  Approve
                </Button>
              </Group>
            </Table.Td>
          </Table.Tr>
        );
      }),
    [requests],
  );

  return (
    <Container fluid>
      <Box pos="relative" p="md">
        <LoadingOverlay visible={loading} />
        <Group justify="space-between" mb="md">
          <Title order={3}>Assign License Requests</Title>
          <Group>
            <Button variant="default" onClick={loadRequests}>
              Refresh
            </Button>
          </Group>
        </Group>
        {error && (
          <Text c="red" mb="sm">
            {error}
          </Text>
        )}
        <ScrollArea>
          <Table stickyHeader striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Request ID</Table.Th>
                <Table.Th>Distributor</Table.Th>
                <Table.Th>Organization</Table.Th>
                <Table.Th>License</Table.Th>
                <Table.Th>Emails to inform</Table.Th>
                <Table.Th>Flags</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollArea>
      </Box>
    </Container>
  );
}
